import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Lock, Save } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PENALTY_NOTIFICATION_POLICIES } from '@/features/content/components/RejectWithPoliciesDialog';
import {
  usePolicyConfidenceThresholds,
  useUpdatePolicyConfidenceThreshold,
  type PolicyConfidenceThreshold,
} from '../api';

type RoutingKey = 'routingClearLt' | 'routingFrontierLt' | 'routingHumanLt';

const ROUTING_FIELDS: { key: RoutingKey; label: string; hint: string }[] = [
  { key: 'routingClearLt', label: 'Clear below', hint: 'Max policy confidence auto-cleared' },
  { key: 'routingFrontierLt', label: 'Frontier below', hint: 'Below this: deep (frontier) scan' },
  { key: 'routingHumanLt', label: 'Human below', hint: 'At/above this: auto-reject' },
];

/** 0–1 → percent string for inputs (keeps one decimal when needed). */
function toPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '';
  return String(Math.round(value * 1000) / 10);
}

/** Percent string → 0–1, or null when blank/out of range/not a number. */
function fromPercent(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  return Math.round(n * 10) / 1000;
}

function formatPercent(value: number | null | undefined): string {
  const p = toPercent(value);
  return p ? `${p}%` : '—';
}

function formatDate(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
}

/** Best-effort proposed attribution (0–1) from a media-manager shadow proposal. */
function proposedValue(proposal: Record<string, unknown> | null | undefined): number | null {
  if (!proposal) return null;
  const candidate = proposal.to ?? proposal.attribution ?? proposal.proposed;
  return typeof candidate === 'number' && Number.isFinite(candidate) ? candidate : null;
}

function ShadowProposal({
  proposal,
  current,
  onApply,
}: {
  proposal: Record<string, unknown> | null | undefined;
  current: number | null;
  onApply: (value: number) => void;
}) {
  if (!proposal) return <span className="text-xs text-muted-foreground">—</span>;
  const to = proposedValue(proposal);
  const from = typeof proposal.from === 'number' ? proposal.from : current;
  const at = formatDate(proposal.at);
  const direction = typeof proposal.direction === 'string' ? proposal.direction : null;
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
          Shadow
        </Badge>
        {to != null ? (
          <span className="text-xs">
            {formatPercent(from)} → <strong>{formatPercent(to)}</strong>
            {direction ? ` (${direction})` : ''}
          </span>
        ) : (
          <span className="font-mono text-[11px] text-muted-foreground">
            {JSON.stringify(proposal)}
          </span>
        )}
      </div>
      {at && <p className="text-[11px] text-muted-foreground">{at}</p>}
      {to != null && (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-1.5 text-[11px]"
          onClick={() => onApply(to)}
        >
          Use proposal
        </Button>
      )}
    </div>
  );
}

function PolicyRow({
  row,
  draft,
  onDraftChange,
  onSave,
  saving,
}: {
  row: PolicyConfidenceThreshold;
  draft: string | undefined;
  onDraftChange: (value: string | undefined) => void;
  onSave: () => void;
  saving: boolean;
}) {
  const serverValue = toPercent(row.attribution);
  const value = draft ?? serverValue;
  const parsed = fromPercent(value);
  const floor = row.safetyFloor;
  const belowFloor = parsed != null && floor != null && parsed < floor;
  const invalid = parsed == null || belowFloor;
  const dirty = draft !== undefined && draft !== serverValue;
  const lastEdit = row.lastManualEdit
    ? formatDate((row.lastManualEdit as Record<string, unknown>).at)
    : null;

  return (
    <TableRow>
      <TableCell className="max-w-[260px] align-top">
        <div className="space-y-1">
          <p className="text-sm font-medium">{row.policyName}</p>
          {lastEdit && (
            <p className="text-[11px] text-muted-foreground">Last manual edit: {lastEdit}</p>
          )}
        </div>
      </TableCell>
      <TableCell className="align-top">
        <div className="flex items-center gap-1">
          <Input
            type="number"
            min={floor != null ? Math.round(floor * 100) : 0}
            max={100}
            step={0.5}
            value={value}
            aria-label={`Attribution threshold for ${row.policyName}`}
            aria-invalid={dirty && invalid}
            onChange={(e) => onDraftChange(e.target.value)}
            className="h-8 w-20 text-xs"
          />
          <span className="text-xs text-muted-foreground">%</span>
        </div>
        {dirty && belowFloor && (
          <p className="mt-1 text-[11px] text-destructive">Min {formatPercent(floor)} (safety floor)</p>
        )}
        {dirty && parsed == null && (
          <p className="mt-1 text-[11px] text-destructive">Enter 0–100</p>
        )}
      </TableCell>
      <TableCell className="align-top">
        <div className="flex flex-wrap items-center gap-1.5">
          {floor != null && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              Floor {formatPercent(floor)}
            </Badge>
          )}
          {row.manualLock && (
            <Badge variant="destructive" className="gap-1 text-[10px] px-1.5 py-0">
              <Lock className="size-3" />
              Manual lock
            </Badge>
          )}
          {floor == null && !row.manualLock && (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </div>
      </TableCell>
      <TableCell className="align-top">
        <ShadowProposal
          proposal={row.shadowProposal}
          current={row.attribution}
          onApply={(v) => onDraftChange(toPercent(v))}
        />
      </TableCell>
      <TableCell className="text-right align-top">
        <Button
          size="sm"
          className="h-8 px-2 text-xs"
          disabled={!dirty || invalid || saving}
          onClick={onSave}
        >
          {saving ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <Save className="size-3" />
          )}
          <span className="ml-1">Save</span>
        </Button>
      </TableCell>
    </TableRow>
  );
}

/**
 * TM-3756 Phase B2: admin-configurable confidence thresholds for the 12 Coop leaf
 * Policies. Attribution is per-Policy; routing bands are shared (one row of inputs).
 */
export function PolicyConfidenceThresholdsPanel() {
  const { data, isLoading, isError, refetch } = usePolicyConfidenceThresholds();
  const update = useUpdatePolicyConfidenceThreshold();

  const [attributionDrafts, setAttributionDrafts] = useState<Record<string, string>>({});
  const [routingDrafts, setRoutingDrafts] = useState<Partial<Record<RoutingKey, string>>>({});
  const [savingPolicy, setSavingPolicy] = useState<string | null>(null);

  // Canonical 12 first (fixed order), then any extra rows media-manager returns.
  const rows = useMemo(() => {
    const byName = new Map((data ?? []).map((r) => [r.policyName, r]));
    const ordered: PolicyConfidenceThreshold[] = [];
    for (const name of PENALTY_NOTIFICATION_POLICIES) {
      const r = byName.get(name);
      if (r) ordered.push(r);
      byName.delete(name);
    }
    return [...ordered, ...byName.values()];
  }, [data]);

  // Routing bands are shared across Policies; read them from the first row.
  const routingSource = rows[0];

  function routingValue(key: RoutingKey): string {
    return routingDrafts[key] ?? toPercent(routingSource?.[key]);
  }

  const routingParsed = {
    routingClearLt: fromPercent(routingValue('routingClearLt')),
    routingFrontierLt: fromPercent(routingValue('routingFrontierLt')),
    routingHumanLt: fromPercent(routingValue('routingHumanLt')),
  };
  const routingDirty = ROUTING_FIELDS.some(
    ({ key }) => routingDrafts[key] !== undefined && routingDrafts[key] !== toPercent(routingSource?.[key]),
  );
  const routingComplete = Object.values(routingParsed).every((v) => v != null);
  const routingOrdered =
    routingComplete &&
    routingParsed.routingClearLt! <= routingParsed.routingFrontierLt! &&
    routingParsed.routingFrontierLt! <= routingParsed.routingHumanLt!;
  const routingError = !routingComplete
    ? 'Enter values between 0 and 100.'
    : !routingOrdered
      ? 'Bands must be ascending: Clear ≤ Frontier ≤ Human.'
      : null;

  async function saveAttribution(row: PolicyConfidenceThreshold) {
    const parsed = fromPercent(attributionDrafts[row.policyName] ?? '');
    if (parsed == null) return;
    setSavingPolicy(row.policyName);
    try {
      await update.mutateAsync({ policyName: row.policyName, attribution: parsed });
      toast.success(`"${row.policyName}" attribution set to ${formatPercent(parsed)}.`);
      setAttributionDrafts((prev) => {
        const next = { ...prev };
        delete next[row.policyName];
        return next;
      });
    } catch {
      toast.error(`Failed to update "${row.policyName}".`);
    } finally {
      setSavingPolicy(null);
    }
  }

  async function saveRouting() {
    if (!routingSource || routingError) return;
    setSavingPolicy('__routing__');
    try {
      // Routing bands are global in media-manager; one PATCH (via the first Policy) updates them.
      await update.mutateAsync({
        policyName: routingSource.policyName,
        routingClearLt: routingParsed.routingClearLt!,
        routingFrontierLt: routingParsed.routingFrontierLt!,
        routingHumanLt: routingParsed.routingHumanLt!,
      });
      toast.success('Routing bands updated.');
      setRoutingDrafts({});
    } catch {
      toast.error('Failed to update routing bands.');
    } finally {
      setSavingPolicy(null);
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-lg">Policy Confidence Thresholds</CardTitle>
            <CardDescription>
              Minimum confidence for each Coop leaf Policy to be attributed to content,
              plus the shared routing bands on the highest Policy confidence.
            </CardDescription>
          </div>
          {data && (
            <Badge variant="outline" className="text-xs">
              {rows.length} Policies
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {isLoading && (
          <div className="flex justify-center py-10">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center gap-3 py-10">
            <p className="text-sm text-destructive">Failed to load Policy confidence thresholds.</p>
            <Button size="sm" variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        )}

        {!isLoading && !isError && rows.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No Policy thresholds configured.
          </p>
        )}

        {!isLoading && !isError && rows.length > 0 && (
          <>
            {/* Shared routing bands */}
            <div className="rounded-md border bg-muted/30 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-medium">Routing bands (shared)</h3>
                  <p className="text-xs text-muted-foreground">
                    Applied to the highest Policy confidence. At or above &ldquo;Human below&rdquo;
                    is auto-rejected once analysis is complete.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-end gap-4">
                {ROUTING_FIELDS.map(({ key, label, hint }) => (
                  <div key={key} className="space-y-1">
                    <label className="text-xs text-muted-foreground" title={hint}>
                      {label}
                    </label>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={routingValue(key)}
                        aria-label={`${label} routing band`}
                        onChange={(e) =>
                          setRoutingDrafts((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                        className="h-8 w-20 text-xs"
                      />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                  </div>
                ))}
                <Button
                  size="sm"
                  className="h-8 px-2 text-xs"
                  disabled={!routingDirty || routingError != null || update.isPending}
                  onClick={() => void saveRouting()}
                >
                  {savingPolicy === '__routing__' ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    <Save className="size-3" />
                  )}
                  <span className="ml-1">Save bands</span>
                </Button>
              </div>
              {routingDirty && routingError && (
                <p className="mt-2 text-xs text-destructive">{routingError}</p>
              )}
            </div>

            {/* Per-Policy attribution thresholds */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Policy</TableHead>
                  <TableHead>Attribution</TableHead>
                  <TableHead>Safety</TableHead>
                  <TableHead>Shadow proposal</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <PolicyRow
                    key={row.policyName}
                    row={row}
                    draft={attributionDrafts[row.policyName]}
                    onDraftChange={(value) =>
                      setAttributionDrafts((prev) => {
                        const next = { ...prev };
                        if (value === undefined) delete next[row.policyName];
                        else next[row.policyName] = value;
                        return next;
                      })
                    }
                    onSave={() => void saveAttribution(row)}
                    saving={savingPolicy === row.policyName}
                  />
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
}
